import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

/* ─── THEME TOKENS ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    userBubble: "#1a1a1a",
    userText: "#ffffff",
    aiBubble: "#f7f7f8",
    aiBorder: "#e8e8eb",
    aiText: "#111111",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    inputBg: "#f7f7f8",
    inputBorder: "#e3e3e6",
    sidebarBg: "#f7f7f8",
    sidebarBorder: "#e3e3e6",
    pillActive: "#1a1a1a",
    pillActiveText: "#ffffff",
    pillBg: "#ebebed",
    pillText: "#5f5f6b",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    userBubble: "#f0f0f0",
    userText: "#111111",
    aiBubble: "#2a2a2a",
    aiBorder: "#3a3a3a",
    aiText: "#ececec",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    inputBg: "#2a2a2a",
    inputBorder: "#3a3a3a",
    sidebarBg: "#1a1a1a",
    sidebarBorder: "#2e2e2e",
    pillActive: "#ececec",
    pillActiveText: "#111111",
    pillBg: "#303030",
    pillText: "#9e9e9e",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

/* ─── DYNAMIC GLOBAL STYLES ─── */
const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; font-family: 'Google Sans Text', 'Google Sans', sans-serif; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 8px; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes spin   { to{transform:rotate(360deg)} }
  @keyframes pulse  { 0%,100%{opacity:1} 50%{opacity:.4} }
  @keyframes dotBounce { 0%,80%,100%{transform:translateY(0);opacity:.3} 40%{transform:translateY(-4px);opacity:1} }

  .fade-up { animation: fadeUp 0.25s ease forwards; }
  .fade-in { animation: fadeIn 0.2s ease; }
  .spin    { animation: spin 1s linear infinite; }
  .pulse   { animation: pulse 1.2s ease infinite; }
  .dot1    { animation: dotBounce 1.2s ease infinite; }
  .dot2    { animation: dotBounce 1.2s ease infinite 0.2s; }
  .dot3    { animation: dotBounce 1.2s ease infinite 0.4s; }

  .sb-btn {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 12px; border-radius: 10px; border: none;
    background: transparent; color: ${t.textSub};
    font-family: 'Google Sans', sans-serif; font-size: 13.5px; font-weight: 500;
    cursor: pointer; width: 100%; text-align: left;
    transition: background 0.12s, color 0.12s, padding-left 0.12s;
  }
  .sb-btn:hover  { background: ${t.surface}; color: ${t.text}; padding-left: 16px; }
  .sb-btn.danger { color: ${t.danger}; }
  .sb-btn.danger:hover { background: ${t.danger}18; }

  .icon-btn {
    display: flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; border-radius: 8px; border: none;
    background: transparent; color: ${t.textSub};
    cursor: pointer; transition: background 0.12s, color 0.12s; flex-shrink: 0;
  }
  .icon-btn:hover         { background: ${t.surface}; color: ${t.text}; }
  .icon-btn:disabled      { opacity: .38; cursor: not-allowed; }
  .icon-btn.mic-live      { color: ${t.danger} !important; background: ${t.danger}14 !important; }

  .send-btn {
    display: flex; align-items: center; justify-content: center;
    width: 38px; height: 38px; border-radius: 10px; border: none;
    cursor: pointer; transition: opacity 0.12s, transform 0.12s; flex-shrink: 0;
    background: ${t.accent}; color: ${t.accentText};
  }
  .send-btn:not(:disabled):hover  { opacity: .82; transform: translateY(-1px); }
  .send-btn:not(:disabled):active { transform: translateY(0); opacity: 1; }
  .send-btn:disabled { background: ${t.surface}; color: ${t.textMuted}; cursor: not-allowed; }

  .chat-input {
    flex: 1; background: transparent; border: none; outline: none;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    font-size: 15px; line-height: 1.55; color: ${t.text};
    padding: 9px 4px; resize: none; min-height: 42px; max-height: 180px;
  }
  .chat-input::placeholder { color: ${t.textMuted}; }

  .chip {
    padding: 7px 14px; border-radius: 20px; cursor: pointer;
    border: 1px solid ${t.border}; background: ${t.bg};
    color: ${t.textSub}; font-size: 13px; font-weight: 500;
    font-family: 'Google Sans', sans-serif;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
  }
  .chip:hover { background: ${t.surface}; color: ${t.text}; border-color: ${t.borderStrong}; }

  .mpill {
    padding: 5px 13px; border-radius: 20px; cursor: pointer;
    border: 1px solid ${t.border}; font-size: 13px; font-weight: 500;
    font-family: 'Google Sans', sans-serif;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
  }
  .mpill-off  { background: ${t.pillBg}; color: ${t.pillText}; }
  .mpill-off:hover { border-color: ${t.borderStrong}; color: ${t.text}; }
  .mpill-on   { background: ${t.pillActive}; color: ${t.pillActiveText}; border-color: ${t.pillActive}; }

  .overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:300; display:flex; align-items:center; justify-content:center; padding:20px; }
  .modal {
    background: ${t.bg}; border: 1px solid ${t.border}; border-radius: 16px;
    padding: 28px; max-width: 500px; width: 100%;
    max-height: 76vh; overflow-y: auto; box-shadow: ${t.shadowMd};
  }

  .md-body p    { margin: 4px 0; line-height: 1.7; }
  .md-body ul, .md-body ol { padding-left: 22px; margin: 6px 0; }
  .md-body li   { margin: 3px 0; }
  .md-body h1,.md-body h2,.md-body h3 { margin: 10px 0 4px; font-weight: 600; font-family: 'Google Sans', sans-serif; }
  .md-body code { font-family: 'Google Sans Mono', monospace; font-size: 12.5px; background: ${t.surface}; padding: 1px 5px; border-radius: 4px; }
  .md-body pre  { background: ${t.surface}; border: 1px solid ${t.border}; border-radius: 10px; padding: 14px; overflow-x: auto; margin: 8px 0; }
  .md-body pre code { background: none; padding: 0; font-size: 12.5px; }
  .md-body table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 13px; }
  .md-body th, .md-body td { border: 1px solid ${t.border}; padding: 6px 10px; }
  .md-body th   { background: ${t.surface}; font-weight: 600; }
  .md-body a    { color: ${t.text}; text-decoration: underline; text-underline-offset: 2px; }
  .md-body blockquote { border-left: 3px solid ${t.border}; padding-left: 12px; color: ${t.textSub}; margin: 8px 0; font-style: italic; }
  .md-body strong { font-weight: 600; }

  .material-icons-round { font-size: 20px; user-select: none; line-height: 1; }
`;

const processMath = (t) =>
  t
    .replace(/\\\\/g, "\\")
    .replace(/\\\(/g, "$")
    .replace(/\\\)/g, "$")
    .replace(/\\\[/g, "$$")
    .replace(/\\\]/g, "$$");

export default function ChatApp() {
  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState("Normie-1");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");

  const T = dark ? THEMES.dark : THEMES.light;

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const recRef = useRef(null);
  const chatRef = useRef([]);

  const email = localStorage.getItem("email");
  const userName = localStorage.getItem("name");
  const userId = localStorage.getItem("userId");
  const loggedIn = !!(email && userName && userId);

  useEffect(() => {
    chatRef.current = chat;
  }, [chat]);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, loading]);

  useEffect(() => {
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  const send = useCallback(
    async (override) => {
      const msg = (override ?? message).trim();
      if (!msg || loading) return;

      setLoading(true);
      setMessage("");
      setInterim("");
      setChat((h) => [...h, { type: "user", text: msg }]);

      const history = chatRef.current
        .filter((i) => i.type === "user" || i.type === "ai")
        .map((i) => ({
          role: i.type === "user" ? "user" : "assistant",
          content: i.text,
        }));

      history.push({ role: "user", content: msg });

      try {
        const url =
          model === "Normie-1"
            ? "https://knaszn5lbvbqjxhxzukyrlgm.agents.do-ai.run/api/v1/chat/completions"
            : "https://ogutbfxcwyc3am7ldrr2sbbd.agents.do-ai.run/api/v1/chat/completions";

        const token =
          model === "Normie-1"
            ? "Bearer pDI5g63vmv2UezMSiX5enoXETKJtkzPk"
            : "Bearer 6qQs7KG8Uum8BwA3mzh6V-jKuym5k4uQ";

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ model, messages: history }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const ai = processMath(data?.choices?.[0]?.message?.content || "No response received.");

        setLoading(false);
        setChat((h) => [...h, { type: "ai", text: ai }]);

        try {
          const { db } = await import("../firebase");
          const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");

          const guestId =
            localStorage.getItem("guestId") ||
            (() => {
              const id = `guest-${crypto.randomUUID()}`;
              localStorage.setItem("guestId", id);
              return id;
            })();

          await addDoc(collection(db, "conversations"), {
            email: loggedIn ? email : null,
            name: loggedIn ? userName : "Guest User",
            userId: loggedIn ? userId : guestId,
            isGuest: !loggedIn,
            message: msg,
            response: ai,
            model,
            timestamp: serverTimestamp(),
          });
        } catch {
          /* silent */
        }
      } catch (err) {
        setChat((h) => [
          ...h,
          {
            type: "ai",
            text: `⚠️ Something went wrong — ${err.message}. Please try again.`,
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [message, loading, model, loggedIn, email, userName, userId]
  );

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (e) => {
      let fin = "";
      let int = "";

      for (let i = e.resultIndex; i < e.results.length; i++) {
        const tx = e.results[i][0].transcript;
        e.results[i].isFinal ? (fin += tx) : (int += tx);
      }

      if (int) setInterim(int);

      if (fin) {
        setMessage(fin);
        setInterim("");
        setListening(false);
        setTimeout(() => send(fin), 150);
      }
    };

    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;

    return () => {
      rec.stop?.();
    };
  }, [send]);

  const toggleMic = async () => {
    if (listening) {
      recRef.current?.stop();
      setListening(false);
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      recRef.current?.start();
      setListening(true);
    } catch {
      alert("Microphone access denied. Please allow it in browser settings.");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const autoGrow = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 180) + "px";
  };

  const Icon = ({ n, style }) => (
    <span className="material-icons-round" style={style}>
      {n}
    </span>
  );

  const Logo = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <span
        style={{
          fontWeight: 700,
          fontSize: 15.5,
          color: T.text,
          fontFamily: "'Google Sans', sans-serif",
          letterSpacing: "-0.3px",
        }}
      >
        Omrali <span style={{ fontWeight: 400, color: T.textSub }}>AI</span>
      </span>
    </div>
  );

  const AiAvatar = () => (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 7,
        flexShrink: 0,
        background: T.accent,
        color: T.accentText,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 12,
        fontFamily: "'Google Sans', sans-serif",
      }}
    >
      O
    </div>
  );

  const UserAvatar = () => (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 7,
        flexShrink: 0,
        background: T.surface,
        border: `1px solid ${T.border}`,
        color: T.textSub,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: 12,
        fontFamily: "'Google Sans', sans-serif",
      }}
    >
      {loggedIn ? userName?.[0]?.toUpperCase() || "U" : <Icon n="person" style={{ fontSize: 16 }} />}
    </div>
  );

  const Sidebar = () => (
    <>
      {mobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 98 }}
          className="fade-in"
        />
      )}

      <aside
        style={{
          width: mobile ? 256 : 224,
          background: T.sidebarBg,
          borderRight: `1px solid ${T.sidebarBorder}`,
          display: "flex",
          flexDirection: "column",
          padding: "14px 8px",
          position: mobile ? "fixed" : "sticky",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 99,
          overflowY: "auto",
          flexShrink: 0,
          transform: mobile ? (sidebarOpen ? "translateX(0)" : "translateX(-100%)") : "none",
          transition: "transform 0.22s cubic-bezier(.4,0,.2,1)",
        }}
      >
        <div style={{ padding: "2px 8px 14px", borderBottom: `1px solid ${T.border}`, marginBottom: 6 }}>
          <Logo />
        </div>

        <div
          style={{
            margin: "0 4px 8px",
            padding: "8px 11px",
            borderRadius: 10,
            background: T.surface,
            border: `1px solid ${T.border}`,
          }}
        >
          {loggedIn ? (
            <>
              <div
                style={{
                  fontSize: 11,
                  color: T.textMuted,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: ".05em",
                }}
              >
                Signed in
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  color: T.text,
                  marginTop: 1,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  fontSize: 11,
                  color: T.textMuted,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: ".05em",
                }}
              >
                Guest mode
              </div>
              <div style={{ fontSize: 12, color: T.textSub, marginTop: 1 }}>Chats are not saved</div>
            </>
          )}
        </div>

        {[
          { icon: "add_comment", label: "New Chat", fn: () => { setChat([]); setMessage(""); } },
          ...(loggedIn
            ? [
                { icon: "person", label: "Profile", fn: () => { window.location.href = "/profile"; } },
                { icon: "history", label: "History", fn: () => { window.location.href = "/history"; } },
              ]
            : []),
          { icon: "info", label: "About", fn: () => { window.location.href = "/about"; } },
          { icon: "mail", label: "Contact", fn: () => { window.location.href = "/contact"; } },
          { icon: "favorite", label: "Donate", fn: () => { window.location.href = "/donate"; } },
          { icon: "bolt", label: "Join Beta", fn: () => { window.location.href = "/waitlist"; } },
          { icon: "download", label: "Download Chats", fn: () => { window.location.href = "/download"; } },
        ].map(({ icon, label, fn }) => (
          <button key={label} className="sb-btn" onClick={() => { fn(); setSidebarOpen(false); }}>
            <Icon n={icon} style={{ fontSize: 18, opacity: 0.75 }} />
            {label}
          </button>
        ))}

        <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "6px 4px" }} />

        <button className="sb-btn" onClick={() => setDark((d) => !d)}>
          <Icon n={dark ? "light_mode" : "dark_mode"} style={{ fontSize: 18, opacity: 0.75 }} />
          {dark ? "Light mode" : "Dark mode"}
        </button>

        <div style={{ flex: 1 }} />

        {loggedIn ? (
          <button
            className="sb-btn danger"
            onClick={() => {
              localStorage.clear();
              window.location.href = "/";
            }}
          >
            <Icon n="logout" style={{ fontSize: 18 }} />
            Logout
          </button>
        ) : (
          <button
            onClick={() => {
              window.location.href = "/login";
            }}
            style={{
              margin: "4px",
              padding: "10px 12px",
              borderRadius: 10,
              background: T.accent,
              border: "none",
              color: T.accentText,
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "'Google Sans', sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <Icon n="login" />
            Sign In
          </button>
        )}

        <div style={{ fontSize: 11, color: T.textMuted, textAlign: "center", padding: "10px 4px 2px" }}>
          © 2025 Omrali AI
        </div>
      </aside>
    </>
  );

  const Bubble = ({ item }) => {
    const isUser = item.type === "user";

    return (
      <div
        style={{
          display: "flex",
          flexDirection: isUser ? "row-reverse" : "row",
          alignItems: "flex-start",
          gap: 9,
          padding: "2px 0",
        }}
      >
        {isUser ? <UserAvatar /> : <AiAvatar />}

        <div
          style={{
            maxWidth: mobile ? "85%" : "72%",
            background: isUser ? T.userBubble : T.aiBubble,
            border: isUser ? "none" : `1px solid ${T.aiBorder}`,
            borderRadius: isUser ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
            padding: "10px 14px",
            fontSize: 14.5,
            lineHeight: 1.65,
            color: isUser ? T.userText : T.aiText,
            boxShadow: isUser ? T.shadow : "none",
            wordBreak: "break-word",
          }}
        >
          {isUser ? (
            <span>{item.text}</span>
          ) : (
            <div className="md-body">
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                {item.text}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    );
  };

  const Typing = () => (
    <div className="fade-up" style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
      <AiAvatar />
      <div
        style={{
          background: T.aiBubble,
          border: `1px solid ${T.aiBorder}`,
          borderRadius: "4px 14px 14px 14px",
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        {[1, 2, 3].map((i) => (
          <span
            key={i}
            className={`dot${i}`}
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: T.textMuted,
              display: "inline-block",
            }}
          />
        ))}
      </div>
    </div>
  );


  const suggestions = [
    "Explain quantum entanglement",
    "Help me debug my code",
    "Balance this equation: H₂ + O₂",
    "Write a short poem about time",
  ];

  useEffect(() => {
    const styleId = "omrali-dynamic-theme";
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = buildCSS(T);
  }, [T]);

  return (
    <>
 
      <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: T.bg, color: T.text }}>
        <Sidebar />

        {mobile && (
          <button
            className="icon-btn"
            onClick={() => setSidebarOpen((p) => !p)}
            style={{
              position: "fixed",
              top: 11,
              left: 11,
              zIndex: 100,
              background: T.bg,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              width: 38,
              height: 38,
            }}
          >
            <Icon n={sidebarOpen ? "close" : "menu"} />
          </button>
        )}

        <main
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: mobile ? "9px 14px 9px 56px" : "9px 20px",
              borderBottom: `1px solid ${T.border}`,
              background: T.bg,
              flexShrink: 0,
              minHeight: 50,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {mobile && <Logo />}
              <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500, marginLeft: mobile ? 0 : 6 }}>
                {model === "Normie-1" ? "General" : "Sarcasm"}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                className={`mpill ${model === "Normie-1" ? "mpill-on" : "mpill-off"}`}
                onClick={() => setModel("Normie-1")}
              >
                Normie-1
              </button>
              <button
                className={`mpill ${model === "Memerali" ? "mpill-on" : "mpill-off"}`}
                onClick={() => setModel("Memerali")}
              >
                Memerali
              </button>

              <button className="icon-btn" onClick={() => setDark((d) => !d)} title="Toggle theme">
                <Icon n={dark ? "light_mode" : "dark_mode"} />
              </button>
            </div>
          </header>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: mobile ? "18px 14px 8px" : "24px 28px 8px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {chat.length === 0 && (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 20,
                  textAlign: "center",
                  padding: "60px 20px",
                }}
                className="fade-in"
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 13,
                    background: T.accent,
                    color: T.accentText,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 20,
                    fontFamily: "'Google Sans', sans-serif",
                    boxShadow: T.shadowMd,
                  }}
                >
                  O
                </div>

                <div>
                  <h1
                    style={{
                      fontSize: mobile ? 22 : 27,
                      fontWeight: 700,
                      color: T.text,
                      letterSpacing: "-0.4px",
                      fontFamily: "'Google Sans', sans-serif",
                    }}
                  >
                    How can I help you today?
                  </h1>
                  <p style={{ color: T.textSub, marginTop: 6, fontSize: 14 }}>
                    {loggedIn ? `Welcome back, ${userName}.` : "You're chatting as a guest."}
                  </p>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 480 }}>
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      className="chip"
                      onClick={() => {
                        setMessage(s);
                        inputRef.current?.focus();
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {chat.map((item, i) => (
              <Bubble key={i} item={item} />
            ))}

            {loading && <Typing />}
            <div ref={bottomRef} style={{ height: 6 }} />
          </div>

          <div style={{ flexShrink: 0, padding: mobile ? "8px 12px 10px" : "10px 28px 12px", background: T.bg }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 4,
                background: T.inputBg,
                border: `1px solid ${T.inputBorder}`,
                borderRadius: 14,
                padding: "4px 6px 4px 8px",
                boxShadow: T.shadow,
              }}
            >
              <button
                className={`icon-btn${listening ? " mic-live" : ""}`}
                onClick={toggleMic}
                title={listening ? "Stop" : "Voice input"}
                style={{ marginBottom: 1 }}
              >
                <Icon n={listening ? "mic_off" : "mic"} />
              </button>

              <textarea
                ref={inputRef}
                className="chat-input"
                rows={1}
                value={listening && interim ? interim : message}
                onChange={(e) => {
                  if (!listening) setMessage(e.target.value);
                  autoGrow(e);
                }}
                onKeyDown={onKey}
                placeholder={listening ? "Listening…" : "Message Omrali AI"}
                disabled={listening}
              />

              <button
                className="send-btn"
                onClick={() => send()}
                disabled={loading || (!message.trim() && !interim)}
                style={{ marginBottom: 1 }}
                title="Send"
              >
                {loading ? (
                  <Icon n="hourglass_empty" style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <Icon n="arrow_upward" />
                )}
              </button>
            </div>

            <div style={{ textAlign: "center", marginTop: 8, fontSize: 11.5, color: T.textMuted, lineHeight: 1.6 }}>
              {listening && (
                <>
                  <span style={{ color: T.danger, fontWeight: 500 }}>
                    <Icon n="mic" style={{ fontSize: 11, verticalAlign: "middle", marginRight: 2 }} />
                    Listening… speak now
                  </span>{" "}
                  ·{" "}
                </>
              )}
              Omrali AI can make mistakes.{" "}
              <button
                onClick={() =>
                window.open(
                  "https://chat.omrali.com/terms",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
                style={{
                  background: "none",
                  border: "none",
                  color: T.textMuted,
                  cursor: "pointer",
                  fontSize: 11.5,
                  fontFamily: "'Google Sans',sans-serif",
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                }}
              >
                Terms
              </button>
              {" · "}
              <button
                onClick={() =>
                window.open(
                  "https://chat.omrali.com/privacy",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
                style={{
                  background: "none",
                  border: "none",
                  color: T.textMuted,
                  cursor: "pointer",
                  fontSize: 11.5,
                  fontFamily: "'Google Sans',sans-serif",
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                }}
              >
                Privacy
              </button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}